import React, { useState, useEffect, useCallback } from "react";
import { getCampaigns, createCampaign, runCampaign, getProspects, deleteCampaign } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Plus, Mail } from "lucide-react";
import CampaignForm from "./campaigns/CampaignForm";
import { CampaignCard, CampaignPreview } from "./campaigns/CampaignCards";
import { log } from "../../lib/log";

const CampaignsPanel = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [running, setRunning] = useState(null);
  const [preview, setPreview] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([getCampaigns(), getProspects()]);
      setCampaigns(c.data);
      setProspects(p.data);
    } catch (err) {
      log.error("Chargement des campagnes :", err);
      toast({ title: "Campagnes indisponibles", description: err?.response?.data?.detail || "Réessayez dans un instant.", variant: "destructive" });
    }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const create = async (payload) => {
    try {
      await createCampaign(payload);
      setShowForm(false);
      load();
      toast({ title: "Campagne créée ✓" });
      return true;
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Création impossible", variant: "destructive" });
      return false;
    }
  };

  const launch = async (id) => {
    setRunning(id);
    try {
      const res = await runCampaign(id);
      toast({ title: "Campagne envoyée", description: `${res.data.sent_count}/${res.data.total} emails envoyés.` });
      load();
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Envoi de campagne échoué", variant: "destructive" });
    } finally {
      setRunning(null);
    }
  };

  const remove = async (id) => {
    await deleteCampaign(id);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold text-xl text-neutral-900">Campagnes email</h2>
        <button onClick={() => setShowForm(!showForm)} data-testid="campaign-new-btn" className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-md">
          <Plus size={16} /> Nouvelle campagne
        </button>
      </div>

      {showForm && <CampaignForm prospects={prospects} onCreate={create} />}

      {campaigns.length === 0 ? (
        <div className="text-center text-neutral-400 py-10">
          <Mail size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">Aucune campagne. Créez-en une pour prospecter en masse par email.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <CampaignCard key={c.id} c={c} running={running === c.id} onPreview={setPreview} onLaunch={launch} onRemove={remove} />
          ))}
        </div>
      )}

      {preview && <CampaignPreview campaign={preview} onClose={() => setPreview(null)} />}
    </div>
  );
};

export default CampaignsPanel;
